import { errorCategoryToIcon, isErrorCategory } from '#/errors/errors';
import { isOneError, OneError } from '#/errors/oneError';
import { OneErrorContext } from '#/hooks/useDataContext';
import { AppStatus, AppStatusIcon } from '#/service/constants';
import { falsyToNull } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

interface UseErrorTranslationsProps {
  fatalError?: boolean;
  status: AppStatus;
  statusTitle?: string;
  statusMessage?: string;
  dismissMessage?: string;
  error?: Error | OneError;
  context: OneErrorContext;
}

// default translations
const appStatusToTranslation: Partial<Record<AppStatus, string>> = {
  [AppStatus.GeoBlocked]: 'app.geoRestriction',
  [AppStatus.Maintenance]: 'app.maintenance',
  [AppStatus.AccessDenied]: 'error.authenticationRequired',
};

export const useErrorTranslations = ({
  error,
  statusTitle,
  statusMessage,
  dismissMessage,
  status,
  fatalError,
  context,
}: UseErrorTranslationsProps) => {
  // if we have a OneError, we use the new error translation keys.
  if (isOneError(error)) {
    const { code } = error;
    const errorCode = code.toLowerCase();

    const [category, area] = errorCode.split('_');
    const errorKey = `errors.${errorCode}`;
    const errorFallbackKey = `errors.${category}_${area}_00`;

    return {
      title: tb([
        `${errorKey}.title`,
        `${errorFallbackKey}.title`,
        'errors.default.title',
      ]),
      message: tb([
        `${errorKey}.message`,
        `${errorFallbackKey}.message`,
        'errors.default.message',
      ]),
      subtext: tb(
        [
          `${errorKey}.subtext`,
          `${errorFallbackKey}.subtext`,
          'errors.default.subtext',
        ],
        context
      ),
      dismiss: falsyToNull(dismissMessage) ?? tb('button.dismiss'),
      statusIcon: isErrorCategory(category)
        ? errorCategoryToIcon[category]
        : undefined,
    };
  }

  // Otherwise, we use the old status component.
  const appStatus = status === AppStatus.Active ? AppStatus.Error : status;
  const translationKey = appStatusToTranslation[appStatus] ?? 'error.generic';

  return {
    title: falsyToNull(statusTitle) ?? tb(`${translationKey}.title`),
    message: falsyToNull(statusMessage) ?? tb(`${translationKey}.message`),
    dismiss: falsyToNull(dismissMessage) ?? tb('button.dismiss'),
    statusIcon: AppStatusIcon[appStatus],
    isActiveAndNotFatal: status === AppStatus.Active && !fatalError,
  };
};
